/**
 * Module for initializing and managing grid tables with sticky columns.
 *
 * This module finds all elements with the `.o-gridTable` class and applies grid layout styles,
 * including sticky positioning for a configurable number of columns. Sticky columns are set using
 * the `data-sticky-cols` attribute on the grid table element. Explicit column widths are read from
 * CSS variables and sticky columns are styled and positioned accordingly.
 *
 * @namespace gridTable
 * @property {Function} init - Initializes all grid tables on the page.
 */
const gridTable = (function () {
    const _settings = {
        debug: globalThis.jsClientSettings?.debug ?? 0,
    };
    function init() {
        alpineCode();
    }

    /**
     * Re-index visible rows inside a grid container.
     * Moved to module scope so multiple Alpine components can call it.
     * @param {HTMLElement} container
     */
    function reIndex(container) {
        const visibleRows = container.querySelectorAll(
            ".o-gridTable__row:not(.o-gridTable__headerRow):not(.o-gridTable__footerRow):not(.-noDisplayRow):not(.-totalRow)"
        );
        visibleRows.forEach((row, idx) => {
            const indexCell = row.querySelector(".o-gridTable__row-indexCell");
            if (indexCell) {
                indexCell.textContent = idx + 1;
            }
        });
    }

    /**
     * Initializes Alpine.js components for grid table functionality and filtering.
     *
     * Registers two Alpine.js components:
     * 1. `gridTable`: Handles grid table setup, sticky columns, sorting, and row limiting.
     *    - Properties:
     *        - rowLimit: Number of visible rows (from data-rowLimit attribute).
     *        - sortOrders: Object tracking sort order for each attribute.
     *        - lastSortAttribute: Last sorted attribute name.
     *    - Methods:
     *        - init(): Initializes the grid table and applies initial sort if specified.
     *        - setupGridTable(gridElement): Configures sticky columns, and applies initial sort.
     *        - sortGridTable(attribute, initialSortAsc): Sorts table rows by the specified attribute and toggles sort order.
     *
     * 2. `gridTableFilter`: Provides interactive filtering for grid tables using form fields.
     *    - Methods:
     *        - init(): Initializes filter logic and attaches event listeners.
     *        - runCombinedFilter(formElement, gridElementID): Attaches listeners to filter fields and runs filtering.
     *        - filterRows(filters, gridElementID): Filters table rows based on attribute/value pairs.
     *
     * @function alpineCode
     * @returns {void}
     */
    function alpineCode() {
        document.addEventListener("alpine:init", () => {
            // delaySetup is needed for cases when you want the table to render first, then apply the gridTable setup (i.e. stats results)
            Alpine.data("gridTable", (sortAttribute = null, sortAsc = true, delaySetup = false) => ({
                rowLimit: null, // Number of visible rows (from data-rowLimit attribute)
                sortOrders: {}, // Object to track sort orders for each attribute
                lastSortAttribute: null, // last sorted attribute

                init() {
                    this.rowLimit = parseInt(this.$root.getAttribute("data-rowLimit"), 10) || null;
                    if (delaySetup) {
                        this.$nextTick(() => {
                            this.setupGridTable(this.$root);
                        });
                    } else {
                        this.setupGridTable(this.$root);
                    }
                },

                updateRowLimit(newLimit) {
                    if (newLimit === null || isNaN(newLimit) || newLimit < 0) {
                        return;
                    }
                    this.rowLimit = newLimit;
                    if (this.rowLimit !== null) {
                        const allRows = this.$root.querySelectorAll(
                            ".o-gridTable__row:not(.o-gridTable__headerRow):not(.o-gridTable__footerRow)"
                        );
                        // Hide rows above new limit
                        allRows.forEach((row, idx) => {
                            if (idx < this.rowLimit) {
                                row.style.display = "";
                            } else {
                                row.style.display = "none";
                            }
                        });
                    }
                    tippySetup.startTippy(); // Reinitialize tippy tooltips after row limit change
                },

                // Custom scroll to element, accounting for sticky columns
                scrollTo(elementId) {
                    const grid = this.$root;
                    const el = document.getElementById(elementId);
                    if (!grid || !el) return;
                    // Find sticky columns width
                    let stickyWidth = 0;
                    const stickyCols = parseInt(grid.dataset.stickyCols) || 0;
                    const headerRow = grid.querySelector(".o-gridTable__row.o-gridTable__headerRow");
                    if (headerRow && stickyCols > 0) {
                        for (let i = 0; i < stickyCols; i++) {
                            const cell = headerRow.children[i];
                            if (cell) {
                                stickyWidth += cell.offsetWidth;
                            }
                        }
                    }
                    // Get the left position of the element relative to the grid
                    const gridRect = grid.getBoundingClientRect();
                    const elRect = el.getBoundingClientRect();
                    const scrollLeft = elRect.left - gridRect.left - stickyWidth;
                    // Use built-in smooth scroll
                    const start = grid.scrollLeft;
                    const end = start + scrollLeft;
                    grid.scrollTo({ left: end, behavior: "smooth" });
                },

                /**
                 * Sets up the grid table layout, sticky columns, row limits, and sorting styles for a given grid element.
                 *
                 * - Applies sticky positioning to the specified number of columns.
                 * - Limits the number of visible rows if `this.rowLimit` is set.
                 * - Calculates column widths based on header cell styles.
                 * - Assigns sticky column classes and left offsets to cells.
                 * - Highlights the sorted column header if `sortAttribute` is provided.
                 *
                 * The grid column layout is provided via CSS variables in the o-gridTable class.
                 *
                 * @param {HTMLElement} gridElement - The grid table element to set up.
                 *
                 * @throws {TypeError} If gridElement is not a Node.
                 *
                 */
                setupGridTable(gridElement) {
                    if (!gridElement || !(gridElement instanceof Node)) {
                        return;
                    }
                    _settings.debug ? console.log("gridTable: Setting up grid table", gridElement.id) : null;
                    const stickyCols = parseInt(gridElement.dataset.stickyCols) || 0;

                    // Limit visible rows if data-rowLimit is set
                    if (this.rowLimit !== null) {
                        const allRows = gridElement.querySelectorAll(
                            ".o-gridTable__row:not(.o-gridTable__headerRow):not(.o-gridTable__footerRow)"
                        );
                        allRows.forEach((row, idx) => {
                            row.style.display = idx < this.rowLimit ? "" : "none";
                        });
                    }

                    // number of columns = number of header cells
                    const headerRow = gridElement.querySelector(".o-gridTable__row.o-gridTable__headerRow");

                    // Get explicit widths from header cells for use in sticky column positioning
                    let colWidths = Array.from(headerRow.children).map((cell) => {
                        // If cell is not a bufferCell, get width
                        if (!cell.classList.contains("bufferCell")) {
                            // Try to get width from inline style first
                            let w = cell.style.width;
                            // Fallback to computed style if inline style not set
                            if (!w) {
                                w = window.getComputedStyle(cell).width;
                            }

                            if (w && w.endsWith("px")) {
                                return w;
                            }
                        }
                        return "";
                    });

                    // Apply sticky positioning
                    // Strategy: measure widths and grid spans in a read-pass, then apply writes in a separate pass
                    _settings.debug ? console.log("gridTable: Setting up sticky column with widths", colWidths) : null;

                    // Compute numeric widths and offsets for sticky columns
                    const numericWidths = [];
                    for (let i = 0; i < stickyCols; i++) {
                        let w = 0;
                        if (colWidths[i] && typeof colWidths[i] === "string" && colWidths[i].endsWith("px")) {
                            w = parseInt(colWidths[i], 10);
                        } else {
                            const headerCell = headerRow.children[i];
                            w = (headerCell && headerCell.offsetWidth) || 120;
                        }
                        numericWidths.push(w);
                    }
                    const offsets = numericWidths.reduce((acc, cur, idx) => {
                        if (idx === 0) {
                            acc.push(0);
                        } else {
                            acc.push(acc[idx - 1] + numericWidths[idx - 1]);
                        }
                        return acc;
                    }, []);

                    // Gather row cell start indices and spans (single read pass per cell)
                    const rows = Array.from(gridElement.querySelectorAll(".o-gridTable__row"));
                    const rowCellMaps = rows.map((row) => {
                        const map = new Map();
                        let colCount = 0;
                        for (let childIdx = 0; childIdx < row.children.length; childIdx++) {
                            const cell = row.children[childIdx];
                            let span = 1;
                            const gridCol = cell.style.gridColumn || window.getComputedStyle(cell).gridColumn;
                            if (gridCol) {
                                const match = gridCol.match(/span\s+(\d+)/);
                                if (match) span = parseInt(match[1], 10);
                            }
                            // record the starting column index for this cell
                            map.set(colCount, cell);
                            colCount += span;
                        }
                        return map;
                    });

                    // Apply sticky classes and styles in a write pass using the gathered maps
                    rows.forEach((row, rowIdx) => {
                        const startMap = rowCellMaps[rowIdx];
                        for (let i = 0; i < stickyCols; i++) {
                            const cell = startMap.get(i);
                            if (!cell) continue;
                            cell.classList.add("sticky-col");
                            const left = offsets[i] ?? 0;
                            const minW = numericWidths[i] ?? 120;
                            cell.style.left = left + "px";
                            cell.style.minWidth = minW + "px";
                            if (row.classList.contains("o-gridTable__headerRow")) cell.classList.add("header");
                            if (row.classList.contains("o-gridTable__footerRow")) cell.classList.add("footer");
                        }
                    });

                    if (sortAttribute) {
                        // Find the header cell matching the sortAttribute and apply -sorted class
                        const headerCells = gridElement.querySelectorAll(".o-gridTable__headerRow > div");
                        headerCells.forEach((cell) => {
                            cell.classList.remove("-sorted");
                            if (cell.dataset && cell.dataset.sortattr && cell.dataset.sortattr === sortAttribute) {
                                cell.classList.add("-sorted");
                                this.lastSortAttribute = sortAttribute;
                                this.sortOrders[sortAttribute] = sortAsc;
                            }
                        });
                    }
                    _settings.debug ? console.log("gridTable: Setup complete for", gridElement.id) : null;
                },

                /**
                 * Sorts the grid table rows based on the specified data attribute.
                 * Toggles sort order when the same attribute is sorted consecutively,
                 * or uses the initial sort order when switching attributes.
                 * Updates the DOM to reflect the new row order and manages row visibility
                 * according to the row limit. Also updates the sorted column's visual state.
                 *
                 * @param {string} attribute - The data attribute to sort by.
                 * @param {boolean} [initialSortAsc=true] - The initial sort order (ascending if true, descending if false).
                 */
                sortGridTable(attribute, initialSortAsc = true) {
                    _settings.debug ? console.log("gridTable: Sorting by", attribute) : null;
                    // Track the last sorted attribute
                    let sortAsc;
                    if (this.lastSortAttribute === attribute) {
                        // Toggle sort order if sorting the same attribute again
                        sortAsc = !this.sortOrders[attribute] ?? initialSortAsc;
                    } else {
                        // Use default sort order when switching attributes
                        sortAsc = initialSortAsc;
                    }
                    const footerRow = this.$root.querySelector(".o-gridTable__footerRow");
                    const rows = Array.from(
                        this.$root.querySelectorAll(
                            ".o-gridTable__row:not(.o-gridTable__headerRow):not(.o-gridTable__footerRow)"
                        )
                    );
                    rows.sort((a, b) => {
                        const aVal = Number(a.dataset[attribute] ?? 0);
                        const bVal = Number(b.dataset[attribute] ?? 0);
                        return sortAsc ? aVal - bVal : bVal - aVal;
                    });

                    rows.forEach((row) => this.$root.removeChild(row));
                    if (footerRow) {
                        this.$root.removeChild(footerRow);
                    }
                    rows.forEach((row) => this.$root.appendChild(row));
                    if (footerRow) {
                        this.$root.appendChild(footerRow);
                    }

                    // Reset row limit after sorting
                    if (this.rowLimit !== null) {
                        rows.forEach((row, idx) => {
                            row.style.display = idx < this.rowLimit ? "" : "none";
                        });
                    }

                    // Re-index the grid table after sorting
                    gridTable.reIndex(this.$root);

                    // Toggle the sort order for subsequent clicks only if sorting the same attribute again
                    if (this.lastSortAttribute === attribute) {
                        this.sortOrders[attribute] = sortAsc;
                    } else {
                        this.sortOrders[attribute] = initialSortAsc;
                    }
                    this.lastSortAttribute = attribute;
                    // Remove -sorted class from any previously sorted element
                    const prevSorted = this.$root.querySelector(".-sorted");
                    if (prevSorted) {
                        prevSorted.classList.remove("-sorted");
                    }
                    // Add -sorted class to the currently sorted column
                    this.$el.classList.add("-sorted");
                },
            }));

            /**
             * Alpine.js gridTableFilter component
             * -----------------------------------
             * Provides interactive filtering for grid tables using Alpine.js.
             * Supports combined filters from multiple form fields (checkboxes, radios, selects, text inputs).
             * Each input/select in the form should have a data-filter-attr attribute indicating the row data attribute to filter on.
             *
             * Properties:
             * - None (uses $root and gridElementID for context)
             *
             * Methods:
             * - init():
             *     Initializes the filter logic by calling runCombinedFilter with the form root and grid element ID.
             * - runCombinedFilter(formElement, containerId):
             *     Attaches event listeners to all filter fields and runs filterRows on change or page load.
             *     @param {HTMLElement} formElement - The form element containing filter fields.
             *     @param {string} containerId - The ID of the grid/table container to filter.
             * - filterRows(filters, containerId):
             *     Filters rows within the container based on attribute/value pairs.
             *     Adds the -noDisplayRow class to rows that do not match any filter.
             *     Removes the -noDisplayRow class from rows that match at least one filter.
             *     @param {Array} filters - Array of filter objects: [{attr: 'team', values: ['NE', 'DET']}]
             *     @param {string} containerId - The ID of the container element to scope the filtering (optional; if not provided, uses document)
             *
             * Example usage:
             *   <form x-data="gridTableFilter('teams')">
             *     ... filter fields ...
             *   </form>
             */
            Alpine.data("gridTableFilter", (gridElementID) => ({
                init() {
                    _settings.debug ? console.log("gridTableFilter: Initializing filter for", gridElementID) : null;
                    this.runCombinedFilter(this.$root, gridElementID);
                },

                /**
                 * runCombinedFilter: Attaches event listeners and runs filterRows for any form fields
                 * @param {string} formId - id for the form containing filter fields
                 * @param {string} gridElementID - id for the container to filter
                 *
                 * Each input/select in the form should have a data-filter-attr attribute indicating the row data attribute to filter on.
                 */
                runCombinedFilter(formElement, gridElementID) {
                    const fields = formElement.querySelectorAll("input[data-filter-attr], select[data-filter-attr]");
                    const self = this;
                    function handler() {
                        let filters = [];
                        let processedAttrs = new Set();

                        fields.forEach((field) => {
                            const attr = field.dataset.filterAttr;
                            if (!attr || processedAttrs.has(attr)) return;
                            let values = [];
                            if (field.type === "checkbox") {
                                const checkboxes = formElement.querySelectorAll(
                                    `input[type="checkbox"][data-filter-attr='${attr}'][name='${field.name}']`
                                );
                                if (checkboxes.length > 1) {
                                    let isOneNotChecked = false;
                                    let allElement = null;
                                    // Multiple checkboxes for this name: include all checked, or empty array if none checked
                                    checkboxes.forEach((cb) => {
                                        if (cb.checked) {
                                            values.push(cb.value);
                                        } else {
                                            isOneNotChecked = true;
                                        }
                                        if (cb.value === "all") {
                                            allElement = cb;
                                        }
                                    });
                                    filters.push({ attr: attr, values: values });
                                    processedAttrs.add(attr);

                                    if (allElement) {
                                        allElement.checked = !isOneNotChecked;
                                    }
                                    return;
                                } else if (checkboxes.length === 1) {
                                    // Single checkbox: include only if checked
                                    if (checkboxes[0].checked) {
                                        values.push(checkboxes[0].value);
                                        filters.push({ attr: attr, values: values });
                                    }
                                    processedAttrs.add(attr);
                                    return;
                                }
                            } else if (field.tagName === "SELECT") {
                                if (field.value !== "") values.push(field.value);
                            } else if (field.type === "radio") {
                                const radios = formElement.querySelectorAll(
                                    `input[type="radio"][data-filter-attr='${attr}'][name='${field.name}']`
                                );
                                radios.forEach((rb) => {
                                    if (rb.checked && rb.value !== "") values.push(rb.value);
                                });
                            } else {
                                if (field.value !== "") values.push(field.value);
                            }
                            processedAttrs.add(attr);
                            // Only add filter if there are values (for non-checkbox fields)
                            if (values.length > 0) {
                                filters.push({ attr: attr, values: values });
                            }
                        });

                        self.filterRows(filters, gridElementID);
                    }
                    fields.forEach((field) => {
                        field.addEventListener("change", handler);
                    });
                    handler(); // Initial filter on page load
                },

                /**
                 * only: helper to check the checkbox with the provided value and uncheck others
                 * @param {string|number} value - the value of the position checkbox to keep checked
                 */
                only(attribute, value) {
                    try {
                        const checkboxes = this.$root.querySelectorAll(
                            `input[type='checkbox'][data-filter-attr='${attribute}']`
                        );
                        let first = null;
                        checkboxes.forEach((cb) => {
                            if (!first) first = cb;
                            const keep = String(cb.value) === String(value);
                            if (keep) {
                                cb.checked = true;
                            } else {
                                cb.checked = false;
                            }
                        });
                        // send change event to trigger filtering
                        if (first) {
                            const event = new Event("change", { bubbles: true });
                            first.dispatchEvent(event);
                        }
                    } catch (e) {
                        console.error("gridTableFilter.only error", e);
                    }
                },

                /**
                 * all: helper to check all checkboxes with the provided attribute
                 * @param {string} attribute - the attribute to filter checkboxes on
                 */
                all(attribute) {
                    try {
                        const checkboxes = this.$root.querySelectorAll(
                            `input[type='checkbox'][data-filter-attr='${attribute}']`
                        );
                        let first = null;
                        checkboxes.forEach((cb) => {
                            if (!first) first = cb;
                            cb.checked = true;
                        });
                        // send change event to trigger filtering
                        if (first) {
                            const event = new Event("change", { bubbles: true });
                            first.dispatchEvent(event);
                        }
                    } catch (e) {
                        console.error("gridTableFilter.all error", e);
                    }
                },

                /**
                 * filterRows: Filters rows within a container based on attribute/value pairs.
                 * Adds the -noDisplayRow class to rows that do not match any filter.
                 * Removes the -noDisplayRow class from rows that match at least one filter.
                 *
                 * @param {Array} filters - Array of filter objects: [{attr: 'team', values: ['Sharks', 'Lions']}]
                 * @param {string} gridElementID - The id of the container element to scope the filtering (optional; if not provided, uses document)
                 * @param {boolean} [reIndex=true] - Whether to resequence row indexes after filtering.
                 *
                 * Example usage:
                 *   filterRows([
                 *     {attr: 'team', values: ['NE', 'DET']},
                 *     {attr: 'position', values: ['1', '2']}
                 *   ], 'myTableContainer');
                 */
                filterRows(filters, gridElementID, reIndex = true) {
                    if (!Array.isArray(filters)) {
                        filters = [filters];
                    }
                    let selector = filters.map((f) => `[data-${f.attr}]`).join(",");
                    // check  if gridElementID exists in DOM
                    let container = document.getElementById(gridElementID);
                    if (!container) {
                        console.warn("Container not found in DOM:", gridElementID);
                        return;
                    }

                    if (selector) {
                        const filteredRows = container.querySelectorAll(selector);
                        for (let i = 0; i < filteredRows.length; i++) {
                            const domRow = filteredRows[i];
                            let match = true;
                            for (let f = 0; f < filters.length; f++) {
                                let dataValue = domRow.dataset[filters[f].attr];

                                if (!filters[f].values.includes(dataValue)) {
                                    match = false;
                                    break;
                                }
                            }
                            if (match) {
                                domRow.classList.remove("-noDisplayRow");
                            } else {
                                domRow.classList.add("-noDisplayRow");
                            }
                        }
                    } else {
                        // remove all -noDisplayRow if no filters
                        const allRows = container.querySelectorAll(".o-gridTable__row");
                        allRows.forEach((row) => row.classList.remove("-noDisplayRow"));
                    }

                    // Resequence row indexes for display after filtering
                    if (reIndex) {
                        gridTable.reIndex(container);
                    }
                },
            }));
        });
    }

    return {
        init: init,
        reIndex: reIndex,
    };
})();

export { gridTable };
